import { Loader2, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import BannerAdSlot from '@/components/BannerAdSlot';
import EarningsDisclaimer from '@/components/EarningsDisclaimer';
import GoogleTranslate from '@/components/GoogleTranslate';
import MobileTopBar from '@/components/member/MobileTopBar';
import Sidebar from '@/components/member/Sidebar';
import AdvertiseSubpage from '@/components/member/subpages/AdvertiseSubpage';
import DashboardSubpage from '@/components/member/subpages/DashboardSubpage';
// Patch v17.5 — TransfersSubpage removed. Peer-to-peer balance transfers
// were permanently dismantled to protect platform liquidity. Any cached
// deep-link visitor is caught by the defensive redirect below (case
// "transfers") and by the backend's 410 Gone response at /api/transfers.
import GlobalPoolSubpage from '@/components/member/subpages/GlobalPoolSubpage';
import LedgerSubpage from '@/components/member/subpages/LedgerSubpage';
import LiveNetworkSubpage from '@/components/member/subpages/LiveNetworkSubpage';
import MemberCycleProofOfWorkSubpage from '@/components/member/subpages/MemberCycleProofOfWorkSubpage';
import MemberProofOfWorkSubpage from '@/components/member/subpages/MemberProofOfWorkSubpage';
import ProfileSubpage from '@/components/member/subpages/ProfileSubpage';
import PromoSubpage from '@/components/member/subpages/PromoSubpage';
import PurchaseSubpage from '@/components/member/subpages/PurchaseSubpage';
import ReferralsSubpage from '@/components/member/subpages/ReferralsSubpage';
import SubPackVaultSubpage from '@/components/member/subpages/SubPackVaultSubpage';
import SupportSubpage from '@/components/member/subpages/SupportSubpage';
import SupportTicketsSubpage from '@/components/member/subpages/SupportTicketsSubpage';
import WithdrawSubpage from '@/components/member/subpages/WithdrawSubpage';
import { useMe } from '@/contexts/MeContext';
import useSubpageParam from '@/hooks/useSubpageParam';

export const SUBPAGES = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'purchase', label: 'Purchase / Deposit' },
  { id: 'withdraw', label: 'Withdrawal' },
  { id: 'subpack_vault', label: 'Sub Pack Vault' },
  { id: 'live_network', label: 'Live Network' },
  { id: 'advertise', label: 'Advertise' },
  { id: 'referrals', label: 'Referrals' },
  // Patch v17.5 — "Transfers" nav item removed (capability permanently dismantled).
  { id: 'global_pool', label: 'Global PIF Pool' },
  { id: 'promo', label: 'Promo Banners' },
  { id: 'ledger', label: 'Transaction History' },
  { id: 'proof_of_work', label: 'Proof of Work' },
  { id: 'cycle_proof_of_work', label: 'Cycle Proof of Work' },
  { id: 'support', label: 'Support' },
  { id: 'profile', label: 'Profile & Security' },
];

// Out of the menu since support moved to email (2026-10-24), but still opens
// at ?subpage=support_tickets in case it moves back.
const HIDDEN_SUBPAGES = [{ id: 'support_tickets', label: 'Support Tickets' }];

const SUBPAGE_IDS = [...SUBPAGES, ...HIDDEN_SUBPAGES].map((s) => s.id);

/**
 * Single-page application shell. State-toggled subpages — no router moves
 * between them, no browser reload. Sidebar on desktop, slide-out drawer on
 * mobile. Google Translate widget mounted in the top header on every view.
 */
const MemberApp = () => {
  const { me, loading, error, refresh } = useMe();
  // `?subpage=<id>` (email deep links) opens that subpage and is kept in sync.
  const [active, setActive] = useSubpageParam(SUBPAGE_IDS);
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (loading) {
    return (
      <div className="sc-canvas min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-3 text-[var(--sc-text-dim)]">
          <Loader2 className="w-5 h-5 animate-spin text-[#FFB612]" />
          Loading your workspace…
        </div>
      </div>
    );
  }

  if (error || !me) {
    return (
      <div className="sc-canvas min-h-screen flex items-center justify-center px-6">
        <div className="sc-glass rounded-2xl p-8 max-w-md text-center">
          <div className="text-[#FFC20E] font-display text-xl mb-2">Session error</div>
          <p className="text-[var(--sc-text-dim)] text-sm mb-6">{String(error || 'Unable to load profile.')}</p>
          <a href="/login" className="sc-btn-gold">
            Back to login
          </a>
        </div>
      </div>
    );
  }

  const renderSubpage = () => {
    switch (active) {
      case 'dashboard':
        return (
          <DashboardSubpage
            me={me}
            onRefresh={refresh}
            onNavigate={(id) => {
              setActive(id);
              setDrawerOpen(false);
            }}
          />
        );
      case 'purchase':
        // Defense in depth — block render if Step 1 isn't done. The
        // sidebar/burger also block the click, but render-gating ensures
        // even forced navigation (browser back-button, deep link) can't
        // reach the deposit/buy actions before sponsor is verified.
        if (!me.sponsor_verified) {
          return (
            <div className="sc-glass rounded-2xl p-8 text-center max-w-xl mx-auto" data-testid="purchase-locked-placeholder">
              <div
                className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4"
                style={{
                  background: 'rgba(255,194,14,0.10)',
                  border: '1px solid rgba(255,194,14,0.42)',
                }}
              >
                <ShieldCheck className="w-6 h-6 text-[#FFC20E]" />
              </div>
              <h3 className="font-display text-xl font-bold">Verify your sponsor first</h3>
              <p className="text-[13px] text-[var(--sc-text-dim)] mt-2 leading-relaxed">
                Before you deposit or purchase positions, please confirm who referred you. This protects your earnings by making sure the right sponsor is on
                file.
              </p>
              <button
                type="button"
                onClick={() => {
                  setActive('dashboard');
                  setDrawerOpen(false);
                }}
                className="sc-btn-gold mt-5"
                style={{ padding: '10px 18px', fontSize: 12 }}
                data-testid="purchase-locked-go-dashboard-btn"
              >
                Go to Dashboard
              </button>
            </div>
          );
        }
        return <PurchaseSubpage me={me} onRefresh={refresh} />;
      case 'withdraw':
        return <WithdrawSubpage me={me} onRefresh={refresh} />;
      case 'subpack_vault':
        return <SubPackVaultSubpage />;
      case 'live_network':
        return <LiveNetworkSubpage />;
      case 'advertise':
        return <AdvertiseSubpage me={me} onRefresh={refresh} />;
      case 'referrals':
        return <ReferralsSubpage me={me} onRefresh={refresh} />;
      case 'transfers':
        // Patch v17.5 — Transfers page permanently removed. If a member
        // reaches this case (e.g. via a stale sessionStorage `active` key
        // or a shared deep-link), silently redirect them to Dashboard on
        // next tick. Rendering null in the meantime is intentional —
        // avoids a flash of anything transfer-related while the state
        // transition completes.
        setTimeout(() => setActive('dashboard'), 0);
        return null;
      case 'global_pool':
        return <GlobalPoolSubpage me={me} onRefresh={refresh} />;
      case 'promo':
        return <PromoSubpage me={me} />;
      case 'ledger':
        return <LedgerSubpage me={me} />;
      case 'proof_of_work':
        return <MemberProofOfWorkSubpage />;
      case 'cycle_proof_of_work':
        return <MemberCycleProofOfWorkSubpage />;
      case 'support':
        return <SupportSubpage me={me} />;
      case 'support_tickets':
        return <SupportTicketsSubpage />;
      case 'profile':
        return <ProfileSubpage me={me} onRefresh={refresh} />;
      default:
        return null;
    }
  };

  const activeMeta = [...SUBPAGES, ...HIDDEN_SUBPAGES].find((s) => s.id === active);

  return (
    <div className="sc-canvas min-h-screen" data-testid="member-app">
      {/* Desktop sidebar */}
      <Sidebar
        active={active}
        onSelect={(id) => {
          setActive(id);
          setDrawerOpen(false);
        }}
        me={me}
      />

      {/* Mobile top bar */}
      <MobileTopBar
        active={active}
        onSelect={(id) => {
          setActive(id);
          setDrawerOpen(false);
        }}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        me={me}
      />

      {/* Main content area */}
      <div className="lg:pl-[280px] pt-16 lg:pt-0 min-h-screen relative z-10">
        {/* Desktop top header (sticky) */}
        <header className="hidden lg:flex sticky top-0 z-30 h-16 px-8 items-center justify-between border-b border-[rgba(255,182,18,0.10)] bg-[rgba(8,8,9,0.78)] backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <h2 className="font-display text-lg font-semibold">{activeMeta?.label || ''}</h2>
            <span className="sc-pill" style={{ padding: '3px 10px', fontSize: 10 }}>
              <span className="sc-dot" />
              Live
            </span>
          </div>
          <GoogleTranslate />
        </header>

        <div className="px-5 md:px-8 py-6 md:py-10 max-w-[1320px] mx-auto">
          {/* Member-area banner stack (under header, above subpage content) */}
          <BannerAdSlot slot="member" count={2} />
          {renderSubpage()}
        </div>

        <EarningsDisclaimer variant="compact" />
      </div>
    </div>
  );
};

export default MemberApp;
